/**
 * Limpeza das chaves de identidade da sessão.
 *
 * Este módulo não importa nada de propósito: ele é usado pelo `services/api`
 * (interceptor de 401) e por hooks que, por sua vez, importam o `api`. Qualquer
 * import aqui arrisca fechar um ciclo.
 */

const TOKEN_API_KEY = '@CANNECT_TOKEN_API'
const USER_KEY = '@CANNECT_USER'
const SIGNED_USER_KEY = '@CANNECT_SIGNED_USER'

/**
 * Chaves que dizem *quem* está logado — e só elas.
 *
 * Fora daqui, deliberadamente:
 *
 * - **Dados de compra** (`@CANNECT:CHECKOUT_CART`, `@CANNECT:ORDER_CHECKOUT_CART`,
 *   seleções de pagamento, `selectedProducts*`). Esta função roda também quando a
 *   sessão expira sozinha (401), que é involuntário: apagar o carrinho ali faria
 *   o paciente montar o pedido, ser mandado para o login e voltar com o carrinho
 *   vazio. Esses dados continuam sendo limpos onde sempre foram — no logout
 *   explícito e na troca de usuário (`usePrepareCart`).
 *
 * - **`@CANNECT:LAST_USER_ID`**. NÃO REMOVA. É a sentinela que o
 *   `usePrepareCart` usa para detectar que quem está logando agora é outra
 *   pessoa e, aí sim, limpar o carrinho anterior. Sem ela o teste
 *   `storedLastUserId !== null` falha e o próximo usuário do mesmo navegador
 *   herda o carrinho de quem saiu.
 *
 * - **`@CANNECT:ANONYMOUS_CART_SESSION`**. É só o `session_cart_id`: o carrinho
 *   do visitante deslogado vive no servidor e essa chave é o ponteiro para ele.
 *   Apagar equivale a jogar o carrinho fora sem conseguir recuperá-lo.
 *
 * - **`@COOKIES_CONSENT`**, que é registro de consentimento e não sessão.
 */
const IDENTITY_KEYS = [TOKEN_API_KEY, USER_KEY, SIGNED_USER_KEY]

export const clearUserIdentity = (): void => {
  try {
    IDENTITY_KEYS.forEach((key) => localStorage.removeItem(key))
  } catch {
    // localStorage indisponível (modo privado//storage bloqueado): não há
    // sessão para limpar e derrubar o boot por causa disso seria pior.
  }
}

/**
 * Sessão pela metade: o token já foi embora mas o usuário ficou no storage.
 *
 * Acontecia quando a sessão expirava — o interceptor de 401 removia só o token
 * e deixava `@CANNECT_USER` para trás. O app então lia `registration_type` dessa
 * chave órfã e se comportava como logado em parte da tela (mostrando preço de
 * cannabis para quem estava deslogado), enquanto as requisições iam pela rota
 * pública.
 *
 * O 401 já não deixa mais esse resto, mas quem passou por ele continua com o
 * storage sujo — daí a checagem no boot.
 */
export const hasOrphanUserIdentity = (): boolean => {
  try {
    return !localStorage.getItem(TOKEN_API_KEY) && !!localStorage.getItem(USER_KEY)
  } catch {
    return false
  }
}

/** Roda no boot, antes do primeiro render, para os providers já subirem limpos. */
export const clearOrphanUserIdentity = (): void => {
  if (hasOrphanUserIdentity()) clearUserIdentity()
}
